import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Line } from 'react-konva';
import PropTypes from 'prop-types';
import React from 'react';
import annTools from './commonAnnotationTools';
import Annotation from './annotation';

// Konva Line wants points to be an array like [x1, y1, x2, y2, x3, y3,... xn, yn]
// so we convert [[x1, y1], [x2, y2], [x3, y3],... [xn, yn]] to that -^
const convertToSimplePoints = points => {
  const simplePointsArray = [];
  points.forEach(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      x = _ref2[0],
      y = _ref2[1];
    simplePointsArray.push(x);
    simplePointsArray.push(y);
  });
  return simplePointsArray;
};

// some pathStrings can have duplicate points, this relates to how the user input the ink,
// annotation. In the end, we need to filter out the duplicate points so they don't have negative
// side-effects in the smoothing algorithm
const removeDuplicatePoints = points => {
  const weededPoints = [];
  points.forEach((_ref3, index) => {
    let _ref4 = _slicedToArray(_ref3, 2),
      thisX = _ref4[0],
      thisY = _ref4[1];
    if (index === 0) {
      weededPoints.push([thisX, thisY]); // push the first point on
    } else {
      const _weededPoints = _slicedToArray(weededPoints[weededPoints.length - 1], 2),
        lastX = _weededPoints[0],
        lastY = _weededPoints[1];
      if (!(lastX === thisX && lastY === thisY)) {
        weededPoints.push([thisX, thisY]); // push this one on as long as it doesn't equal the last
      }
    }
  });
  return weededPoints;
};

// must be defined outside of the component to avoid re-rendering loops
const defaultOnDragEnd = () => {};
const defaultHandleEdit = () => {};
const Ink = _ref5 => {
  let _ref5$draggable = _ref5.draggable,
    draggable = _ref5$draggable === void 0 ? false : _ref5$draggable,
    scale = _ref5.scale,
    pageHeight = _ref5.pageHeight,
    offset = _ref5.offset,
    dpiScale = _ref5.dpiScale,
    selectedID = _ref5.selectedID,
    annotation = _ref5.annotation,
    _ref5$onDragEnd = _ref5.onDragEnd,
    onDragEnd = _ref5$onDragEnd === void 0 ? defaultOnDragEnd : _ref5$onDragEnd,
    _ref5$handleEdit = _ref5.handleEdit,
    handleEdit = _ref5$handleEdit === void 0 ? defaultHandleEdit : _ref5$handleEdit;
  const _annotation$inkAnnota = annotation.inkAnnotation,
    pathString = _annotation$inkAnnota.pathString,
    width = _annotation$inkAnnota.width,
    isLine = _annotation$inkAnnota.unsmoothed,
    colorString = annotation.colorString,
    id = annotation.id;

  // only care about the first one
  const _annTools$adaptPDFRec = annTools.adaptPDFRects(scale, pageHeight, annotation, offset),
    _annTools$adaptPDFRec2 = _slicedToArray(_annTools$adaptPDFRec, 1),
    box = _annTools$adaptPDFRec2[0];

  // convert the pdf points string into an array of [x, y] points
  const adaptedPoints = annTools.adaptPathStringToCanvas(pathString, pageHeight, scale, dpiScale, offset);

  // weeds out points that are duplicates - dupes mess up the smoothing algorithms
  const weededPoints = removeDuplicatePoints(adaptedPoints);

  // only allow ink scaling if there are a min. of 2 points to scale and there is a width and height
  // (lines can only have 2 points)
  const allowScaling = !!isLine || !!(box.h && box.w && weededPoints.length >= 2);
  // only smooth if it's not line, the width/height is at least 10 and there are 4 or more points
  const shouldSmooth = !isLine && !!(box.h > 10 && box.w > 10 && weededPoints.length >= 4);
  const strokeWidth = width * scale / dpiScale;
  const lineJoin = 'round';
  const lineCap = isLine ? 'square' : 'round';
  const color = annTools.parseColor(colorString);
  const colorRGBAString = "rgba(".concat(color[0], ",").concat(color[1], ",").concat(color[2], ",0.70)");
  const ends = isLine ? [weededPoints[0], weededPoints[weededPoints.length - 1]] : [];

  // implementation detail from Konva, we need at least 2pts to render a `line`
  const pointsToPlot = weededPoints.length === 1 ? [...convertToSimplePoints(weededPoints), ...convertToSimplePoints(weededPoints)] : convertToSimplePoints(weededPoints);
  return /*#__PURE__*/React.createElement(Annotation, {
    scalable: allowScaling,
    draggable: draggable,
    box: box,
    selectedID: selectedID,
    annotationID: id,
    dpiScale: dpiScale,
    scale: scale,
    onDragEnd: onDragEnd,
    onDragMove: handleEdit,
    ends: ends
  }, /*#__PURE__*/React.createElement(Line, {
    key: "".concat(id, "-0"),
    points: pointsToPlot,
    stroke: colorRGBAString,
    strokeWidth: strokeWidth,
    lineJoin: lineJoin,
    lineCap: lineCap,
    bezier: shouldSmooth
  }));
};
Ink.propTypes = {
  annotation: PropTypes.shape({
    colorString: PropTypes.string,
    id: PropTypes.string,
    inkAnnotation: PropTypes.shape({
      pathString: PropTypes.string,
      // annotation tools parses the string into an array
      width: PropTypes.number,
      unsmoothed: PropTypes.number
    })
  }).isRequired,
  dpiScale: PropTypes.number.isRequired,
  draggable: PropTypes.bool,
  offset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }).isRequired,
  onDragEnd: PropTypes.func,
  handleEdit: PropTypes.func,
  pageHeight: PropTypes.number.isRequired,
  scale: PropTypes.number.isRequired,
  selectedID: PropTypes.string.isRequired
};
export default Ink;